'use client';

import { useSetState } from 'ahooks';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch, useGlobalModal } from '@/app/hooks';
import { reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { followsFollow, followsUnfollow } from '../apis';

export interface FollowResult {
  targetUid: string;
  followed: boolean;
  action: 'follow' | 'unfollow';
}

export interface FollowState {
  isFollowing: boolean;
  isMutual?: boolean;
  loading?: boolean;
}

export interface UseFollowStateOptions {
  /** 初始关注状态 */
  initialIsFollowing?: boolean;
  /** 初始互相关注状态 */
  initialIsMutual?: boolean;
  /** 关注状态变化回调 */
  onFollowChange?: (result: FollowResult) => void;
  /** 是否显示确认对话框（取关时） */
  showConfirmDialog?: boolean;
  /** 确认对话框配置 */
  confirmDialogConfig?: {
    title: string;
    content: string;
    secondaryButtonText: string;
    primaryButtonText: string;
  };
}

export interface UseFollowStateReturn {
  /** 当前关注状态 */
  state: FollowState;
  /** 更新关注状态 */
  updateState: (updates: Partial<FollowState>) => void;
  /** 处理关注操作 */
  handleFollow: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** 直接关注 */
  follow: (targetUid: string) => void;
  /** 直接取关 */
  unfollow: (targetUid: string) => void;
  /** 是否正在加载 */
  loading: boolean;
}

export const useFollow = (userId: string, options: UseFollowStateOptions = {}): UseFollowStateReturn => {
  const t = useTranslations();
  const pathname = usePathname();

  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');

  const {
    initialIsFollowing = false,
    initialIsMutual = false,
    onFollowChange,
    showConfirmDialog = true,
    confirmDialogConfig,
  } = options;

  // 保存初始的互相关注状态，用于状态计算
  const initialIsMutualRef = useRef(initialIsMutual);

  const [state, setState] = useSetState<FollowState>({
    isFollowing: false,
    isMutual: false,
    loading: false,
  });

  useEffect(() => {
    setState({
      isFollowing: initialIsFollowing || false,
      isMutual: initialIsMutual || false,
    });
  }, [initialIsMutual, initialIsFollowing]);

  const { run: executeFollow, loading: followLoading } = useFetch(
    ({ targetUid, action }: { targetUid: string; action: 'follow' | 'unfollow' }) => {
      return action === 'follow'
        ? followsFollow({
            path: { target_uid: targetUid },
            body: { target_uid: targetUid },
          })
        : followsUnfollow({
            path: { target_uid: targetUid },
          });
    },
    {
      manual: true,
      onBefore: (params: [{ targetUid: string; action: 'follow' | 'unfollow' }]) => {
        const { targetUid, action } = params[0];
        reportEvent('follow_click', {
          object_id: targetUid,
          click_type: action === 'follow' ? 'follow' : 'cancel_follow',
          page_name: pathname.includes('profile') ? 'profile' : 'detail',
        });
      },
      onSuccess: ({ error }, params: [{ targetUid: string; action: 'follow' | 'unfollow' }]) => {
        const { targetUid, action } = params[0];
        const followed = action === 'follow';

        if (error) {
          setState({
            isFollowing: !followed,
            isMutual: !followed ? initialIsMutualRef.current : false,
            loading: false,
          });

          onFollowChange?.({
            targetUid,
            followed: !followed,
            action,
          });

          toast({
            type: 'error',
            message: t('popup.toast.operationFailed'),
          });
          return;
        }

        if (followed) {
          toast({
            type: 'success',
            message: t('profile.followSuccess'),
          });
        }

        setState({
          isFollowing: followed,
          isMutual: followed ? initialIsMutualRef.current : false,
          loading: false,
        });

        onFollowChange?.({
          targetUid,
          followed,
          action,
        });
      },
    },
  );

  const updateState = useCallback(
    (updates: Partial<FollowState>) => {
      setState((prevState) => ({ ...prevState, ...updates }));
    },
    [setState],
  );

  const handleFollow = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (state.isFollowing) {
        if (showConfirmDialog) {
          const config = confirmDialogConfig || {
            title: t('profile.confirmDialog.title'),
            content: t('profile.confirmDialog.content'),
            secondaryButtonText: t('profile.confirmDialog.primaryButtonText'),
            primaryButtonText: t('profile.confirmDialog.secondaryButtonText'),
          };

          openConfirmDialog({
            title: config.title,
            closable: false,
            content: config.content,
            secondaryButtonText: config.secondaryButtonText,
            primaryButtonText: config.primaryButtonText,
            onSecondaryAction: () => executeFollow({ targetUid: userId, action: 'unfollow' }),
          });
        } else {
          executeFollow({ targetUid: userId, action: 'unfollow' });
        }
      } else {
        executeFollow({ targetUid: userId, action: 'follow' });
      }
    },
    [state.isFollowing, showConfirmDialog, confirmDialogConfig, t, openConfirmDialog, userId, executeFollow],
  );

  const follow = useCallback(
    (targetUid: string) => {
      executeFollow({ targetUid, action: 'follow' });
    },
    [executeFollow],
  );

  const unfollow = useCallback(
    (targetUid: string) => {
      executeFollow({ targetUid, action: 'unfollow' });
    },
    [executeFollow],
  );

  return {
    state,
    updateState,
    handleFollow,
    follow,
    unfollow,
    loading: followLoading || state.loading || false,
  };
};
